import { type Friend, friendKeys, upsertFriendInCache } from "@/entities/friend"
import {
	dismissNotificationsWhere,
	showNotification,
} from "@/entities/notification"
import type { SocialMatchOfferNotificationData } from "@/shared/api/generated-social"
import { registerNotification } from "@/shared/api/websocket"
import { socialMatchKeys } from "../model/keys"

function toProposal(
	notificationId: number,
	data: SocialMatchOfferNotificationData,
) {
	return {
		proposalId: data.proposal_id,
		friend: data.friend,
		notificationId,
		simulation: {
			total: data.simulation_results.total,
			conflict: data.simulation_results.conflict,
			bored: data.simulation_results.bored,
			great: data.simulation_results.great,
			longTermChance: data.simulation_results.long_term_chance,
		},
	}
}

function dismissSupersededProposals({
	proposalId,
	friend,
}: {
	proposalId: number
	friend: Friend
}) {
	dismissNotificationsWhere(
		(r) =>
			"proposalId" in r &&
			(r.proposalId === proposalId || r.friend.id === friend.id),
	)
}

// skippable → кандидату («Пропустить»/«Добавить»).
registerNotification("social_match_skippable_notification", {
	handle: ({ notification_id, data }, { queryClient, userId }) => {
		const proposal = toProposal(notification_id, data)
		dismissSupersededProposals(proposal)
		queryClient.invalidateQueries({
			queryKey: socialMatchKeys.status(userId),
		})
		showNotification({
			type: "social-match-candidate",
			placement: "home",
			...proposal,
			origin: "server",
		})
	},
	route: () => ({ to: "/home" }),
	routePopup: null,
})

// unskippable → заказчику/instant-кандидату (без «Пропустить»). Мэтч уже
// состоялся → друг есть на бэке: обновляем список, чтобы чат не был пустым.
registerNotification("social_match_unskippable_notification", {
	handle: ({ notification_id, data }, { queryClient, userId }) => {
		const proposal = toProposal(notification_id, data)
		dismissSupersededProposals(proposal)
		queryClient.invalidateQueries({
			queryKey: socialMatchKeys.status(userId),
		})
		upsertFriendInCache(queryClient, userId, proposal.friend)
		queryClient.invalidateQueries({
			queryKey: friendKeys.list(userId),
			refetchType: "none",
		})
		showNotification({
			type: "social-match-found",
			placement: "home",
			...proposal,
			origin: "server",
		})
	},
	route: () => ({ to: "/home" }),
	routePopup: null,
})

// revoked → отзыв предложения: снимаем устаревшие candidate/found того же
// proposalId и показываем чёрный попап «просрочен».
registerNotification("social_match_revoked_notification", {
	handle: ({ notification_id, data }) => {
		dismissNotificationsWhere(
			(r) => "proposalId" in r && r.proposalId === data.proposal_id,
		)
		showNotification({
			type: "social-match-expired",
			placement: "home",
			origin: "server",
			notificationId: notification_id,
		})
	},
	route: () => ({ to: "/home" }),
	routePopup: null,
})

registerNotification("social_match_timeout_notification", {
	handle: ({ notification_id }, { queryClient, userId }) => {
		queryClient.invalidateQueries({
			queryKey: socialMatchKeys.status(userId),
		})
		showNotification({
			type: "social-match-not-found",
			placement: "home",
			origin: "server",
			notificationId: notification_id,
		})
	},
	route: () => ({ to: "/home" }),
	routePopup: null,
})

// rejected → админ отклонил собственную заявку юзера. Бэк вернул спин
// (attempts) бесплатнику → инвалидируем статус (кнопка потеряет замок).
registerNotification("social_match_rejected_notification", {
	handle: ({ notification_id }, { queryClient, userId }) => {
		queryClient.invalidateQueries({
			queryKey: socialMatchKeys.status(userId),
		})
		showNotification({
			type: "social-match-rejected",
			placement: "home",
			origin: "server",
			name: "social_match_rejected_notification",
			notificationId: notification_id,
		})
	},
	route: () => ({ to: "/home" }),
	routePopup: null,
})
